import React, { FC } from 'react';
import {
  FormControl,
  Box,
  Text,
  MultiSelectCheckbox,
  TextFieldSize,
  TextFieldAppearance,
  Icon,
  IconSize,
} from '@bechtel/gds';
import { useField } from 'formik';
import { useLingui } from '@lingui/react';
import { EllipsisPlacement, TextWithEllipsis } from '@bechtel/client-common';
import { EllipseStyled } from '../weld-sub-header/styled';
import { WeldTagItemProps, WeldTagSelectorProps } from './types';
import { t, Trans } from '@lingui/macro';

const WeldTagSelectedItem = ({ selectedItemCount }: any) => {
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }} px={{ sm: '200' }}>
      <Box mr={{ sm: '400' }}>
        <Text family="graphik" weight="600" size="400" color="grey.1000">
          {selectedItemCount}
        </Text>
        &nbsp;
        <Text family="graphik" size="400" color="grey.1000">
          <Trans>welds selected</Trans>
        </Text>
      </Box>
    </Box>
  );
};

export const WeldTagItem: FC<WeldTagItemProps> = ({ item, showDelete, onDelete }) => {
  return (
    <Box
      px={{ sm: '500' }}
      justifyContent={{ sm: 'space-between' }}
      flexGrow={{ sm: 1 }}
      alignItems={{ sm: 'center' }}
      css={{ cursor: 'pointer' }}
      onClick={() => {
        if (!showDelete || !onDelete) return;
        onDelete(item.id);
      }}
    >
      <Box alignItems={{ sm: 'center' }} flexDirection={{ sm: 'column' }} mr={{ sm: '500' }}>
        <TextWithEllipsis
          ellipsisPlacement={EllipsisPlacement.END}
          family="graphik"
          size="500"
          transform="uppercase"
          color="grey.1000"
          css={{
            maxWidth: '180px',
          }}
        >
          {item.tag}
        </TextWithEllipsis>
        <Box alignSelf={{ sm: 'flex-start' }} mt={{ sm: '300' }}>
          {item?.pipeMaterial && (
            <>
              <Box mr={{ sm: '400' }}>
                <Text size="300" color="grey.700">
                  {item?.pipeMaterial}
                </Text>
              </Box>
              <EllipseStyled />
            </>
          )}
          {item?.type?.name && (
            <>
              <Box mr={{ sm: '400' }}>
                <Text size="300" color="grey.700">
                  {item?.type?.name}
                </Text>
              </Box>
              <EllipseStyled />
            </>
          )}
          {item?.pipeDiameter && item?.pipeDiameter > 0 ? (
            <Text size="300" color="grey.700">
              {`${item?.pipeDiameter}” ${t`Dia`}`}
            </Text>
          ) : null}
        </Box>
      </Box>
      {showDelete && (
        <Box mr={{ sm: '450' }}>
          <Icon icon="times-circle" fillColor="bechtel.blue.800" size={IconSize.MEDIUM} />
        </Box>
      )}
    </Box>
  );
};

export const WeldTagSelector: FC<WeldTagSelectorProps> = ({ loading, items, fieldName, onSearch }) => {
  const { i18n } = useLingui();
  const [field, meta, helpers] = useField(fieldName);
  const { setValue } = helpers;
  return (
    <Box data-testid="weld-tag-selector" flexDirection={{ sm: 'column' }} px={{ sm: '450' }} pt={{ sm: '500' }}>
      <FormControl style={{ flexGrow: 1 }} label="">
        <MultiSelectCheckbox
          {...field}
          initialSelectedItems={field.value}
          showSearchBar={true}
          items={items}
          itemComponent={WeldTagItem}
          selectedComponent={WeldTagSelectedItem}
          size={TextFieldSize.LARGE}
          showSelectAll={false}
          confirmSelection={true}
          displayKeyField="id"
          appearance={TextFieldAppearance.NORMAL}
          placeholder={t`Select Weld`}
          loading={loading}
          onSelectedItemsChange={(selectedWelds) => {
            setValue([...selectedWelds]);
          }}
          onSearchInputChange={(searchQuery: string) => {
            onSearch(searchQuery);
          }}
        />
      </FormControl>
      {meta.touched && meta.error ? (
        <Box pt={{ sm: '200' }}>
          <Text color="bechtel.red">{i18n._(meta.error)}</Text>
        </Box>
      ) : null}
    </Box>
  );
};
